import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  BookOpen, Briefcase, TrendingUp, Cpu, Shield, Palette, LineChart, Cog,
  Stethoscope, Smile, Pill, HeartPulse, Scale, Building2, Sprout, Utensils,
  Newspaper, Atom, GraduationCap, Music, Plane, PawPrint, ArrowRight, Lock,
  Library, Accessibility, Trophy, Wrench, Search, X,
} from "lucide-react";
import { VERTICALS } from "../data/verticals";

const ICONS = {
  BookOpen, Briefcase, TrendingUp, Cpu, Shield, Palette, LineChart, Cog,
  Stethoscope, Smile, Pill, HeartPulse, Scale, Building2, Sprout, Utensils,
  Newspaper, Atom, GraduationCap, Music, Plane, PawPrint,
  Library, Accessibility, Trophy, Wrench,
};

// Returns a match reason string if the vertical matches the query, else null.
function matchReason(v, q) {
  if (!q) return "";
  const inName = v.name.toLowerCase().includes(q) || (v.category || "").toLowerCase().includes(q);
  if (inName) return "";
  const col = (v.colleges || []).find((c) => c.name.toLowerCase().includes(q) || (c.location || "").toLowerCase().includes(q));
  if (col) return `College: ${col.name}`;
  const exam = (v.exams || []).find((e) => e.name.toLowerCase().includes(q));
  if (exam) return `Exam: ${exam.name}`;
  const spec = (v.specializations || []).find((s) => s.toLowerCase().includes(q));
  if (spec) return `Specialization: ${spec}`;
  const deg = (v.degrees || []).find((d) => d.name.toLowerCase().includes(q));
  if (deg) return `Degree: ${deg.name}`;
  return null;
}

export default function HigherEducation() {
  const [query, setQuery] = useState("");
  const live = VERTICALS.filter((v) => !v.comingSoon);
  const soon = VERTICALS.filter((v) => v.comingSoon);
  const q = query.trim().toLowerCase();

  const results = useMemo(
    () => live.map((v) => ({ v, reason: matchReason(v, q) })).filter((r) => r.reason !== null),
    [q] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Header />
      <main>
        <section className="bg-[#16244F] text-white" data-testid="verticals-hero">
          <div className="max-w-[1240px] mx-auto px-5 py-16 lg:py-20">
            <p className="font-head text-sm font-semibold text-[#F58220] tracking-wide uppercase mb-3">
              Higher Education in India
            </p>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl">
              22 Verticals of Higher Education
            </h1>
            <p className="text-base md:text-lg text-white/70 mt-5 max-w-2xl">
              Explore every major stream after Class 12 — degrees, specializations, entrance exams
              and the top-ranked colleges across India. Pick a vertical to dive in.
            </p>
            <div className="flex items-center gap-6 mt-8 text-sm text-white/60">
              <span><span className="text-white font-semibold">{live.length}</span> live now</span>
              <span className="w-px h-4 bg-white/20" />
              <span><span className="text-white font-semibold">{soon.length}</span> coming soon</span>
            </div>
            <div className="relative mt-8 max-w-xl" data-testid="verticals-search">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a stream, college or entrance exam…"
                data-testid="vertical-search-input"
                className="w-full bg-white text-[#16244F] placeholder:text-[#8a90a0] rounded-xl pl-11 pr-11 py-3.5 text-sm outline-none focus:ring-2 focus:ring-[#F58220]"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  data-testid="vertical-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a90a0] hover:text-[#16244F]"
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="max-w-[1240px] mx-auto px-5 py-14">
          {q ? (
            <>
              <h2 className="font-head text-lg font-bold text-[#16244F] mb-6" data-testid="verticals-results-heading">
                {results.length} {results.length === 1 ? "result" : "results"} for “{query}”
              </h2>
              {results.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center text-[#8a90a0]" data-testid="verticals-no-results">
                  No verticals match that search. Try a stream, college or exam name.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="verticals-grid">
                  {results.map(({ v, reason }) => {
                    const Icon = ICONS[v.icon] || BookOpen;
                    return (
                      <Link
                        key={v.slug}
                        to={`/higher-education/${v.slug}`}
                        data-testid={`vertical-card-${v.slug}`}
                        className="group bg-white rounded-2xl p-6 cc-shadow-card hover:-translate-y-1 transition-transform border border-transparent hover:border-[#F58220]/30"
                      >
                        <div className="flex items-start justify-between">
                          <div className="w-12 h-12 rounded-xl bg-[#16244F]/5 grid place-items-center text-[#16244F] group-hover:bg-[#F58220] group-hover:text-white transition-colors">
                            <Icon size={22} />
                          </div>
                          <ArrowRight size={18} className="text-[#c3c8d4] group-hover:text-[#F58220] group-hover:translate-x-1 transition-all" />
                        </div>
                        <span className="inline-block mt-5 text-[11px] font-semibold uppercase tracking-wide text-[#F58220]">{v.category}</span>
                        <h3 className="font-head text-lg font-bold text-[#16244F] mt-1 leading-snug">{v.name}</h3>
                        {reason && (
                          <p className="mt-2 text-xs text-[#45506b] bg-[#F58220]/10 inline-block px-2 py-1 rounded" data-testid={`vertical-match-${v.slug}`}>{reason}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-[#8a90a0]">
                          <span>{v.degrees?.length || 0} degrees</span>
                          <span>{v.colleges?.length || 0} colleges</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
          <>
          <h2 className="font-head text-lg font-bold text-[#16244F] mb-6">Available now</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="verticals-grid">
            {live.map((v) => {
              const Icon = ICONS[v.icon] || BookOpen;
              return (
                <Link
                  key={v.slug}
                  to={`/higher-education/${v.slug}`}
                  data-testid={`vertical-card-${v.slug}`}
                  className="group bg-white rounded-2xl p-6 cc-shadow-card hover:-translate-y-1 transition-transform border border-transparent hover:border-[#F58220]/30"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-[#16244F]/5 grid place-items-center text-[#16244F] group-hover:bg-[#F58220] group-hover:text-white transition-colors">
                      <Icon size={22} />
                    </div>
                    <ArrowRight size={18} className="text-[#c3c8d4] group-hover:text-[#F58220] group-hover:translate-x-1 transition-all" />
                  </div>
                  <span className="inline-block mt-5 text-[11px] font-semibold uppercase tracking-wide text-[#F58220]">{v.category}</span>
                  <h3 className="font-head text-lg font-bold text-[#16244F] mt-1 leading-snug">{v.name}</h3>
                  <div className="flex items-center gap-4 mt-4 text-xs text-[#8a90a0]">
                    <span>{v.degrees?.length || 0} degrees</span>
                    <span>{v.colleges?.length || 0} colleges</span>
                  </div>
                </Link>
              );
            })}
          </div>

          <h2 className="font-head text-lg font-bold text-[#16244F] mt-14 mb-6" style={{ display: soon.length ? "block" : "none" }}>Coming soon</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="verticals-soon">
            {soon.map((v) => {
              const Icon = ICONS[v.icon] || BookOpen;
              return (
                <div
                  key={v.slug}
                  data-testid={`vertical-soon-${v.slug}`}
                  className="bg-white/60 rounded-2xl p-5 border border-dashed border-gray-200 opacity-80"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#16244F]/5 grid place-items-center text-[#9aa1b2]">
                      <Icon size={18} />
                    </div>
                    <Lock size={14} className="text-[#c3c8d4]" />
                  </div>
                  <h3 className="font-head text-sm font-bold text-[#45506b] mt-3 leading-snug">{v.name}</h3>
                  <span className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-wide text-[#aab0bd]">Coming soon</span>
                </div>
              );
            })}
          </div>
          </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
